import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Mail, Send, Search, Users, Building2, CheckCircle2, AlertCircle, Clock, Inbox as InboxIcon, PenSquare, Paperclip, ChevronLeft } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'https://api.orbit-technology.com/api';
const MAX_RECIPIENTS = 50;

// Direct email to existing clients and lenders only. Recipients are picked
// from records - there is no free-typed address field, and the server resolves
// every address itself, so this cannot be used for cold outreach. Replies come
// back into the Inbox tab: every email carries a reply address unique to its
// thread (see backend services/messagingInbox.js).
export default function Messaging() {
  const [view, setView] = useState('inbox');
  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="mb-5 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight flex items-center gap-2.5">
            <Mail size={22} className="text-blue-600" /> Messaging
          </h1>
          <p className="text-gray-400 text-sm mt-0.5">
            Email existing clients and lenders directly. Only addresses already on file can be reached; replies come back to your Orbit inbox.
          </p>
        </div>
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 self-start">
          {[['inbox', 'Inbox', InboxIcon], ['compose', 'Compose', PenSquare]].map(([key, label, Icon]) => (
            <button key={key} onClick={() => setView(key)}
              className={`flex items-center gap-1.5 text-sm px-4 py-1.5 rounded-lg font-medium transition-colors ${view === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
      </div>
      {view === 'inbox' ? <MessagingInbox onCompose={() => setView('compose')} /> : <Compose />}
    </div>
  );
}

function MessagingInbox({ onCompose }) {
  const { user, token } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState('');
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const loadThreads = () => {
    fetch(`${API}/messaging/threads${showAll ? '?all=1' : ''}`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(d => setThreads(Array.isArray(d) ? d : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setLoading(true);
    loadThreads();
    const interval = setInterval(loadThreads, 30000);
    return () => clearInterval(interval);
  }, [showAll]);

  const openThread = async id => {
    setError('');
    setReply('');
    try {
      const res = await fetch(`${API}/messaging/threads/${id}`, { headers });
      if (!res.ok) throw new Error('Could not open conversation');
      setActive(await res.json());
      fetch(`${API}/messaging/threads/${id}/read`, { method: 'POST', headers })
        .then(() => setThreads(ts => ts.map(t => (t.id === id && t.ownerId === user.id ? { ...t, unread: 0 } : t))))
        .catch(() => {});
    } catch (e) { setError(e.message); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !active) return;
    setSending(true);
    setError('');
    try {
      const res = await fetch(`${API}/messaging/threads/${active.id}/reply`, {
        method: 'POST', headers, body: JSON.stringify({ body: reply.trim() }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.error || 'Reply failed');
      setReply('');
      await openThread(active.id);
      loadThreads();
    } catch (e) { setError(e.message); }
    finally { setSending(false); }
  };

  const download = async (msg, att) => {
    try {
      const res = await fetch(`${API}/messaging/threads/${active.id}/messages/${msg.id}/attachments/${att.index}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Download failed');
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = att.filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { setError(e.message); }
  };

  const filtered = threads.filter(t => {
    const q = search.trim().toLowerCase();
    return !q || [t.contactName, t.lastSubject, t.ownerName, ...(t.contactEmails || [])].some(v => String(v || '').toLowerCase().includes(q));
  });
  const canReply = active && active.ownerId === user?.id;

  return (
    <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden flex flex-col md:flex-row" style={{ height: '640px' }}>
      {/* Thread list */}
      <div className={`w-full md:w-80 border-b md:border-b-0 md:border-r border-gray-100 flex flex-col shrink-0 ${active ? 'hidden md:flex' : 'flex'} md:h-full h-full`}>
        <div className="p-3 border-b border-gray-100 space-y-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search conversations..."
              className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
          </div>
          {isAdmin && (
            <label className="flex items-center gap-2 text-xs text-gray-500 px-1">
              <input type="checkbox" checked={showAll} onChange={e => { setShowAll(e.target.checked); setActive(null); }} /> Show every rep's inbox
            </label>
          )}
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <p className="text-gray-400 text-xs p-4 text-center">Loading...</p>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-gray-400 text-xs mb-3">No conversations yet. Replies to emails you send from Messaging show up here.</p>
              <button onClick={onCompose} className="text-blue-600 text-xs font-medium hover:underline">Compose an email</button>
            </div>
          ) : filtered.map(t => (
            <button key={t.id} onClick={() => openThread(t.id)}
              className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${active?.id === t.id ? 'bg-blue-50' : ''}`}>
              <div className="flex items-center justify-between gap-2">
                <p className={`text-sm truncate ${t.unread ? 'font-semibold text-gray-900' : 'font-medium text-gray-800'}`}>
                  {t.contactName}
                  <span className="ml-1.5 text-[10px] uppercase tracking-wide text-gray-400 font-medium">{t.contactType}</span>
                </p>
                {t.unread > 0
                  ? <span className="min-w-[20px] h-5 px-1.5 bg-blue-600 rounded-full text-white text-xs flex items-center justify-center shrink-0">{t.unread}</span>
                  : <span className="text-gray-400 text-[11px] shrink-0">{new Date(t.lastAt).toLocaleDateString()}</span>}
              </div>
              <p className="text-gray-500 text-xs truncate mt-0.5">{t.lastSubject}</p>
              <p className="text-gray-400 text-xs truncate">
                {showAll ? `${t.ownerName} · ` : ''}{t.lastDirection === 'out' ? 'You: ' : ''}{t.lastSnippet}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Conversation */}
      <div className={`flex-1 flex flex-col min-w-0 ${active ? 'flex' : 'hidden md:flex'}`}>
        {!active ? (
          <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">Select a conversation</div>
        ) : (
          <>
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-3">
              <button onClick={() => setActive(null)} className="text-gray-400 hover:text-gray-700 md:hidden"><ChevronLeft size={18} /></button>
              <div className="min-w-0">
                <p className="text-gray-900 text-sm font-semibold truncate">{active.contactName}</p>
                <p className="text-gray-400 text-xs truncate">{(active.contactEmails || []).join(', ')}{active.ownerId !== user?.id ? ` · ${active.ownerName}'s inbox` : ''}</p>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50/50">
              {active.messages.map(m => {
                const out = m.direction === 'out';
                return (
                  <div key={m.id} className={`flex ${out ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${out ? 'bg-blue-600 text-white' : 'bg-white border border-gray-100 text-gray-900'}`}>
                      <p className={`text-xs mb-1 ${out ? 'text-blue-100' : 'text-gray-500'}`}>
                        {out ? (m.fromName || 'You') : (m.fromName ? `${m.fromName} <${m.fromEmail}>` : m.fromEmail)}
                        {' · '}{new Date(m.at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </p>
                      {m.subject && <p className={`text-xs font-semibold mb-1 ${out ? 'text-white' : 'text-gray-700'}`}>{m.subject}</p>}
                      <p className="whitespace-pre-wrap break-words">{m.text || (m.fullText ? m.fullText : '(no text)')}</p>
                      {!out && (m.unverifiedSender || m.spam) && (
                        <p className="mt-2 text-[11px] text-amber-700 bg-amber-50 rounded-lg px-2 py-1">
                          {m.spam ? 'Flagged as possible spam. ' : ''}{m.unverifiedSender ? 'Sent from an address not on this contact\'s record.' : ''}
                        </p>
                      )}
                      {!out && (m.attachments || []).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {m.attachments.map(a => (
                            <button key={a.index} onClick={() => download(m, a)}
                              className="flex items-center gap-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg px-2 py-1">
                              <Paperclip size={11} /> {a.filename}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {error && <div className="mx-3 mt-2 flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2"><AlertCircle size={14} /> {error}</div>}
            {canReply ? (
              <div className="p-3 border-t border-gray-100 flex items-end gap-2">
                <textarea value={reply} onChange={e => setReply(e.target.value)} rows={2} maxLength={10000}
                  placeholder={`Reply to ${active.contactName}...`}
                  className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 resize-y" />
                <button onClick={sendReply} disabled={sending || !reply.trim()}
                  className="h-10 px-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 rounded-xl flex items-center gap-1.5 text-white text-sm font-medium transition-colors">
                  <Send size={14} /> {sending ? 'Sending...' : 'Reply'}
                </button>
              </div>
            ) : (
              <p className="p-3 border-t border-gray-100 text-xs text-gray-400 text-center">Viewing {active.ownerName}'s conversation - only they can reply.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Compose() {
  const { user, token } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [type, setType] = useState('client');
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const loadHistory = () => {
    fetch(`${API}/messaging/history`, { headers }).then(r => r.ok ? r.json() : []).then(d => setHistory(Array.isArray(d) ? d : [])).catch(() => {});
  };

  useEffect(() => {
    setLoading(true);
    setSelected(new Set());
    setResult(null);
    setError('');
    fetch(`${API}/messaging/recipients?type=${type}`, { headers })
      .then(async r => {
        const d = await r.json().catch(() => null);
        if (!r.ok) throw new Error((d && d.error) || `Could not load recipients (${r.status})`);
        return d;
      })
      .then(d => setRecipients(Array.isArray(d) ? d : []))
      .catch(e => { setRecipients([]); setError(e.message); })
      .finally(() => setLoading(false));
  }, [type]);

  useEffect(loadHistory, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return recipients;
    return recipients.filter(r =>
      [r.name, r.businessName, r.status, r.assignedRepName, ...(r.emails || [])]
        .some(v => String(v || '').toLowerCase().includes(q)));
  }, [recipients, search]);

  const sendable = r => r.emails?.length > 0 && !r.doNotEmail;

  const toggle = id => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else if (next.size < MAX_RECIPIENTS) next.add(id);
      return next;
    });
  };

  const selectAllShown = () => {
    setSelected(prev => {
      const next = new Set(prev);
      for (const r of filtered) {
        if (next.size >= MAX_RECIPIENTS) break;
        if (sendable(r)) next.add(r.id);
      }
      return next;
    });
  };

  const send = async () => {
    setError('');
    setResult(null);
    if (!selected.size || !subject.trim() || !body.trim()) return;
    const noun = type === 'lender' ? 'lender' : 'client';
    if (!window.confirm(`Send this email to ${selected.size} ${noun}${selected.size === 1 ? '' : 's'}?`)) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/messaging/send`, {
        method: 'POST', headers,
        body: JSON.stringify({ type, ids: [...selected], subject: subject.trim(), body: body.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Send failed');
      setResult(data);
      setSelected(new Set());
      loadHistory();
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  const mergeFields = type === 'lender' ? ['{{lender_name}}'] : ['{{first_name}}', '{{business_name}}'];

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {[['client', 'Clients', Users], ['lender', 'Lenders', Building2]].map(([key, label, Icon]) => (
          <button key={key} onClick={() => setType(key)}
            className={`flex items-center gap-1.5 text-sm px-4 py-2 rounded-xl font-medium transition-colors ${type === key ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Recipients */}
        <div className="lg:col-span-2 bg-white border border-gray-100 rounded-2xl shadow-sm flex flex-col" style={{ maxHeight: '620px' }}>
          <div className="p-3 border-b border-gray-100 space-y-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder={type === 'lender' ? 'Search lenders...' : 'Search clients, status, rep...'}
                className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-500">{selected.size} selected (max {MAX_RECIPIENTS})</span>
              <div className="flex gap-3">
                <button onClick={selectAllShown} className="text-blue-600 hover:underline">Select shown</button>
                <button onClick={() => setSelected(new Set())} className="text-gray-500 hover:underline">Clear</button>
              </div>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <p className="text-gray-400 text-xs p-4 text-center">Loading...</p>
            ) : filtered.length === 0 ? (
              <p className="text-gray-400 text-xs p-4 text-center">No {type === 'lender' ? 'lenders' : 'clients'} found</p>
            ) : filtered.map(r => {
              const ok = sendable(r);
              return (
                <label key={r.id}
                  className={`flex items-start gap-3 px-4 py-2.5 border-b border-gray-50 ${ok ? 'cursor-pointer hover:bg-gray-50' : 'opacity-50 cursor-not-allowed'} ${selected.has(r.id) ? 'bg-blue-50' : ''}`}>
                  <input type="checkbox" className="mt-1" disabled={!ok} checked={selected.has(r.id)} onChange={() => toggle(r.id)} />
                  <div className="min-w-0">
                    <p className="text-gray-900 text-sm font-medium truncate">{r.businessName || r.name}</p>
                    <p className="text-gray-400 text-xs truncate">
                      {type === 'client' && r.name ? `${r.name} · ` : ''}
                      {r.doNotEmail ? 'Do not email' : (r.emails?.length ? r.emails.join(', ') : 'No email on file')}
                    </p>
                    {type === 'client' && (r.status || r.assignedRepName) && (
                      <p className="text-gray-400 text-xs truncate">{[r.status, r.assignedRepName].filter(Boolean).join(' · ')}</p>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {/* Compose */}
        <div className="lg:col-span-3 bg-white border border-gray-100 rounded-2xl shadow-sm p-4 flex flex-col gap-3">
          <input value={subject} onChange={e => setSubject(e.target.value)} maxLength={200} placeholder="Subject"
            className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
          <textarea value={body} onChange={e => setBody(e.target.value)} maxLength={10000} rows={12} placeholder="Write your message..."
            className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 resize-y" />
          <p className="text-gray-400 text-xs">
            Personalize with {mergeFields.map((f, i) => (
              <button key={f} onClick={() => setBody(b => b + f)} className="font-mono text-blue-600 hover:underline mr-1">{f}{i < mergeFields.length - 1 ? ',' : ''}</button>
            ))}. Each recipient gets their own email.
          </p>
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2"><AlertCircle size={14} /> {error}</div>
          )}
          {result && (
            <div className="text-sm bg-gray-50 rounded-xl px-3 py-2 space-y-1">
              <p className="flex items-center gap-2 text-gray-900 font-medium">
                <CheckCircle2 size={14} className="text-green-600" />
                {result.sent} sent{result.skipped ? `, ${result.skipped} skipped` : ''}{result.failed ? `, ${result.failed} failed` : ''}
              </p>
              {result.results.filter(x => x.status !== 'sent').map(x => (
                <p key={x.id} className="text-gray-500 text-xs">{x.name}: {x.status} - {x.reason}</p>
              ))}
            </div>
          )}
          <div className="flex justify-end">
            <button onClick={send} disabled={sending || !selected.size || !subject.trim() || !body.trim()}
              className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm px-5 py-2.5 rounded-xl transition-colors font-medium">
              <Send size={14} /> {sending ? 'Sending...' : `Send to ${selected.size || 0}`}
            </button>
          </div>
        </div>
      </div>

      {/* History */}
      <div className="mt-6 bg-white border border-gray-100 rounded-2xl shadow-sm">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
          <Clock size={14} className="text-gray-400" />
          <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">Sent{isAdmin ? ' (everyone)' : ''}</p>
        </div>
        {history.length === 0 ? (
          <p className="text-gray-400 text-xs p-4 text-center">Nothing sent yet</p>
        ) : history.map(h => {
          const sent = (h.results || []).filter(x => x.status === 'sent').length;
          return (
            <div key={h.id} className="px-4 py-3 border-b border-gray-50">
              <div className="flex items-center justify-between gap-3">
                <p className="text-gray-900 text-sm font-medium truncate">{h.subject}</p>
                <p className="text-gray-400 text-xs shrink-0">{new Date(h.sentAt).toLocaleString()}</p>
              </div>
              <p className="text-gray-400 text-xs truncate">
                {isAdmin ? `${h.senderName} · ` : ''}{h.type === 'lender' ? 'Lenders' : 'Clients'} · {sent} of {(h.results || []).length} sent · {(h.results || []).map(x => x.name).join(', ')}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
